import { useQuery } from '@tanstack/react-query';
import { apiService as mockApi } from '@/api';
import type { DeviceFilters } from '@/types';

export function useDevices(filters: DeviceFilters) {
  return useQuery({
    queryKey: ['devices', filters],
    queryFn: () => mockApi.getDevices(filters),
  });
}

export function useDevice(id: string | undefined) {
  return useQuery({
    queryKey: ['device', id],
    queryFn: () => mockApi.getDevice(id!),
    enabled: !!id,
    refetchInterval: 15000,
  });
}

export function useClients() {
  return useQuery({ queryKey: ['clients'], queryFn: () => mockApi.getClients() });
}

export function useSites() {
  return useQuery({ queryKey: ['sites'], queryFn: () => mockApi.getSites() });
}

export function useAlerts(filters: { severity?: string; state?: string; search?: string } = {}) {
  return useQuery({ queryKey: ['alerts', filters], queryFn: () => mockApi.getAlerts(filters) });
}

export function useAlertRules() {
  return useQuery({ queryKey: ['alert-rules'], queryFn: () => mockApi.getAlertRules() });
}

export function useScripts() {
  return useQuery({ queryKey: ['scripts'], queryFn: () => mockApi.getScripts() });
}

export function useJobs() {
  return useQuery({ queryKey: ['jobs'], queryFn: () => mockApi.getJobs() });
}

export function useUsers() {
  return useQuery({ queryKey: ['users'], queryFn: () => mockApi.getUsers() });
}

export function useRoles() {
  return useQuery({ queryKey: ['roles'], queryFn: () => mockApi.getRoles() });
}

export function useAuditLog(filters: { category?: string; result?: string; search?: string } = {}) {
  return useQuery({ queryKey: ['audit-log', filters], queryFn: () => mockApi.getAuditLog(filters) });
}

export function useRemoteSessions() {
  return useQuery({ queryKey: ['remote-sessions'], queryFn: () => mockApi.getRemoteSessions() });
}

export function usePatchPolicies() {
  return useQuery({ queryKey: ['patch-policies'], queryFn: () => mockApi.getPatchPolicies() });
}

export function useOverviewMetrics() {
  return useQuery({ queryKey: ['overview-metrics'], queryFn: () => mockApi.getOverviewMetrics() });
}

export function useActivityFeed() {
  return useQuery({ queryKey: ['activity-feed'], queryFn: () => mockApi.getActivityFeed() });
}

export function useDeviceFiles(deviceId: string, path: string) {
  return useQuery({ queryKey: ['files', deviceId, path], queryFn: () => mockApi.listFiles(deviceId, path) });
}

export function useDeviceDrives(deviceId: string) {
  return useQuery({ queryKey: ['drives', deviceId], queryFn: () => mockApi.getDrives(deviceId) });
}
